import { useState, useMemo, useCallback, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useRateHawkStays } from "../ratehawk/useRateHawkStays";
import type {
  RateHawkSearchParams,
  RateHawkSearchFilter,
  RateHawkSearchResult,
  RateHawkRate,
  RateHawkRoomGroup,
  RateHawkRoomGuestConfig,
} from "../ratehawk/types";
import type { AccommodationActivity } from "../models/activity.model";
import type { IGuest } from "@/lib/request-trip/models/travel-request.model";
import {
  buildRateHawkGuests,
  countGuestsFromRateHawkData,
  convertRateHawkToPlace2BeAccommodation,
} from "../ratehawk/ratehawk-accommodation/helpers";
import { SearchWithResults } from "../ratehawk/ratehawk-accommodation/SearchWithResults";
import { RatesView } from "../ratehawk/ratehawk-accommodation/RatesView";

type Step = "search" | "rates";

interface RateHawkAccommodationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<AccommodationActivity>) => void;
  destinations?: string[];
  startDate?: string | null;
  endDate?: string | null;
  currentDayDate?: string;
  guestsData?: IGuest[];
  totalGuests?: number;
}

export function RateHawkAccommodationModal({
  isOpen,
  onClose,
  onSave,
  destinations,
  startDate,
  endDate,
  currentDayDate,
  guestsData,
  totalGuests,
}: RateHawkAccommodationModalProps) {
  const {
    searchResults,
    totalHotels,
    isSearching,
    searchError,
    searchStays,
    roomGroups,
    selectedHotel,
    isLoadingRates,
    ratesError,
    fetchRates,
    clearSearch,
    clearRates,
    isMockMode,
  } = useRateHawkStays();

  const [step, setStep] = useState<Step>("search");
  const [residency, setResidency] = useState("it");

  // ── Search form state ──────────────────────────────────────────────────────
  const [cityName, setCityName] = useState("");
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [checkIn, setCheckIn] = useState(currentDayDate?.split("T")[0] || "");
  const [checkOut, setCheckOut] = useState("");
  const [rooms, setRooms] = useState<RateHawkRoomGuestConfig[]>([{ adults: 2, childrenAges: [] }]);
  const [radius, setRadius] = useState(10);
  const [filter, setFilter] = useState<RateHawkSearchFilter>({});

  // Seed a single room from the trip's known guest list (adults/children ages).
  const buildInitialRooms = useCallback((): RateHawkRoomGuestConfig[] => {
    const { adults, childrenAges: ages } = countGuestsFromRateHawkData(
      guestsData as { age?: number }[] | undefined,
      totalGuests,
    );
    return [{ adults, childrenAges: ages }];
  }, [guestsData, totalGuests]);

  // ── Sync on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setCityName("");
      setCoordinates(null);
      setCheckIn(currentDayDate?.split("T")[0] || "");
      setCheckOut("");
      setRooms(buildInitialRooms());
      setRadius(10);
      setFilter({});
      setStep("search");
      clearSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const rateHawkGuests = useMemo(() => buildRateHawkGuests(rooms), [rooms]);

  const showRoomsHint = useMemo(() => {
    if (rooms.length <= 1) return false;
    const first = rooms[0];
    return !rooms.every(
      (r) => r.adults === first.adults && r.childrenAges.length === first.childrenAges.length,
    );
  }, [rooms]);

  const canSearch = isMockMode
    ? !!(checkIn && checkOut)
    : !!(coordinates && checkIn && checkOut);

  const handleSearch = useCallback(async () => {
    if (!checkIn || !checkOut) return;

    const params: RateHawkSearchParams = {
      checkin: checkIn,
      checkout: checkOut,
      guests: rateHawkGuests,
      latitude: coordinates?.lat ?? 41.9028, // fallback: Rome (mock only)
      longitude: coordinates?.lng ?? 12.4964,
      radius: radius * 1000, // km → metres
      currency: "EUR",
      residency: residency,
      filter,
    };

    await searchStays(params);
  }, [coordinates, checkIn, checkOut, rateHawkGuests, radius, filter, searchStays]);

  const handleSelectResult = useCallback(
    (result: RateHawkSearchResult) => {
      setStep("rates");
      fetchRates(result, {
        checkin: checkIn,
        checkout: checkOut,
        guests: rateHawkGuests,
        latitude: coordinates?.lat ?? 0,
        longitude: coordinates?.lng ?? 0,
        radius: radius * 1000,
        currency: "EUR",
        residency: residency,
      });
    },
    [fetchRates, checkIn, checkOut, rateHawkGuests, coordinates, radius],
  );

  const handleSelectRate = useCallback(
    (rate: RateHawkRate, room: RateHawkRoomGroup) => {
      if (!selectedHotel) return;

      const activity = convertRateHawkToPlace2BeAccommodation({
        rate,
        room,
        hotel: selectedHotel,
        checkInDate: checkIn,
        checkOutDate: checkOut,
        selectedRooms: rooms,
      });

      onSave(activity);
      handleClose();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedHotel, checkIn, checkOut, onSave],
  );

  const handleClose = useCallback(() => {
    setStep("search");
    setCityName("");
    setCoordinates(null);
    setCheckIn(currentDayDate?.split("T")[0] || "");
    setCheckOut("");
    setRooms(buildInitialRooms());
    setRadius(10);
    setFilter({});
    setResidency("it");
    clearSearch();
    onClose();
  }, [clearSearch, onClose, currentDayDate, buildInitialRooms]);

  const handleBack = useCallback(() => {
    if (step === "rates") {
      clearRates();
      setStep("search");
    }
  }, [step, clearRates]);

  const title =
    step === "search"
      ? "Search Accommodations"
      : selectedHotel?.name || "Room Rates";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          {step !== "search" && (
            <Button variant="ghost" size="sm" onClick={handleBack} className="p-1">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <span>{title}</span>
        </div>
      }
      size="full"
    >
      {step === "search" && (
        <SearchWithResults
          cityName={cityName}
          setCityName={setCityName}
          coordinates={coordinates}
          setCoordinates={setCoordinates}
          checkIn={checkIn}
          setCheckIn={setCheckIn}
          checkOut={checkOut}
          setCheckOut={setCheckOut}
          rooms={rooms}
          setRooms={setRooms}
          radius={radius}
          setRadius={setRadius}
          filter={filter}
          onFilterChange={setFilter}
          totalHotels={totalHotels}
          canSearch={canSearch}
          isSearching={isSearching}
          onSearch={handleSearch}
          results={searchResults}
          searchError={searchError}
          onSelectResult={handleSelectResult}
          startDate={startDate}
          endDate={endDate}
          residency={residency}
          onResidencyChange={setResidency}
        />
      )}

      {step === "rates" && (
        <RatesView
          roomGroups={roomGroups}
          hotel={selectedHotel}
          isLoading={isLoadingRates}
          error={ratesError}
          onSelect={handleSelectRate}
          showRoomsHint={showRoomsHint}
        />
      )}
    </Modal>
  );
}
